import {ReactElement, useEffect, useMemo, useState} from "react";
import {Body, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {ReactComponent as SearchIcon} from "@zendeskgarden/svg-icons/src/16/search-stroke.svg";
import {ReactComponent as AssignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-right-fill.svg";
import {ReactComponent as UnassignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-left-fill.svg";
import {
    ListPayrollJurisdictionsQuery,
    useSetPropertyJurisdictionMutation
} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import * as css from "./styles/jurisdictionStyles.module.scss";
import {itemId, useMultiSelect} from "../../../../utils/hooks/useMultiSelect";

interface PropertyItem {
    id: string;
    name: string;
}

interface JurisdictionPropertiesModalProps {
    jurisdiction: {
        id: string;
        name: string;
        payrollPropertyJurisdiction: {propertyId: string}[];
    };
    allProperties: PropertyItem[];
    allJurisdictions: ListPayrollJurisdictionsQuery["listPayrollJurisdictions"];
    closed: (changed: boolean) => void;
}

export function JurisdictionPropertiesModal(props: JurisdictionPropertiesModalProps): ReactElement {
    const {jurisdiction, allProperties, allJurisdictions} = props;

    const [setPropertyJurisdiction] = useSetPropertyJurisdictionMutation();
    const [saving, setSaving] = useState(false);

    const originalAssignedIds = useMemo(() => {
        return new Set(jurisdiction.payrollPropertyJurisdiction.map(p => p.propertyId));
    }, [jurisdiction]);

    const propertyJurisdictionMap = useMemo(() => {
        const map = new Map<string, string>();
        for(const j of allJurisdictions) {
            for(const pj of j.payrollPropertyJurisdiction) {
                map.set(pj.propertyId, j.name);
            }
        }
        return map;
    }, [allJurisdictions]);

    const [assignedProperties, setAssignedProperties] = useState<PropertyItem[]>(() => {
        return allProperties.filter(p => originalAssignedIds.has(p.id));
    });

    const [availableProperties, setAvailableProperties] = useState<PropertyItem[]>(() => {
        return allProperties.filter(p => !originalAssignedIds.has(p.id));
    });

    const [availableSearch, setAvailableSearch] = useState("");
    const [assignedSearch, setAssignedSearch] = useState("");
    const [hideAlreadyAssigned, setHideAlreadyAssigned] = useState(true);

    const [filteredAvailable, setFilteredAvailable] = useState<PropertyItem[]>(availableProperties);
    const [filteredAssigned, setFilteredAssigned] = useState<PropertyItem[]>(assignedProperties);
    const availableSelection = useMultiSelect(filteredAvailable, itemId);
    const assignedSelection = useMultiSelect(filteredAssigned, itemId);
    const selectedAvailableProperties = availableSelection.selected;
    const selectedAssignedProperties = assignedSelection.selected;

    useEffect(() => {
        let filtered = availableProperties;
        if(hideAlreadyAssigned) {
            filtered = filtered.filter(p => !propertyJurisdictionMap.has(p.id));
        }
        if(availableSearch !== "") {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(availableSearch.toLowerCase())
            );
        }
        setFilteredAvailable(filtered);
    }, [availableSearch, availableProperties, hideAlreadyAssigned]);

    useEffect(() => {
        if(assignedSearch === "") {
            setFilteredAssigned(assignedProperties);
        } else {
            setFilteredAssigned(assignedProperties.filter(p =>
                p.name.toLowerCase().includes(assignedSearch.toLowerCase())
            ));
        }
    }, [assignedSearch, assignedProperties]);

    function assignProperties(): void {
        availableSelection.setSelected([]);
        assignedSelection.setSelected(prev => [...selectedAvailableProperties, ...prev, ]);
        setAvailableProperties(prev => prev.filter(p => !selectedAvailableProperties.some(x => x.id === p.id)));
        setAssignedProperties(prev => [...selectedAvailableProperties, ...prev]);
    }

    function unassignProperties(): void {
        availableSelection.setSelected(prev => [...selectedAssignedProperties, ...prev]);
        assignedSelection.setSelected([]);
        setAvailableProperties(prev => [...selectedAssignedProperties, ...prev]);
        setAssignedProperties(prev => prev.filter(p => !selectedAssignedProperties.some(x => x.id === p.id)));
    }

    function doSave(): void {
        const currentAssignedIds = new Set(assignedProperties.map(p => p.id));

        const propertyIds = assignedProperties
            .filter(p => !originalAssignedIds.has(p.id))
            .map(p => p.id);

        const removePropertyIds = Array.from(originalAssignedIds)
            .filter(id => !currentAssignedIds.has(id));

        if(propertyIds.length === 0 && removePropertyIds.length === 0) {
            props.closed(false);
            return;
        }

        setSaving(true);
        setPropertyJurisdiction({
            variables: {
                propertyIds,
                removePropertyIds,
                jurisdictionId: jurisdiction.id
            }
        }).then(() => {
            toast.success("Property associations updated!");
            props.closed(true);
        }).catch(() => {/* Do Nothing */})
            .finally(() => setSaving(false));
    }

    return <Modal style={{width: "800px", maxWidth: "90vw"}}>
        <Header>
            Manage Properties for &quot;{jurisdiction.name}&quot;
        </Header>
        <Body className={css.modalBody}>
            <div className={css.dualListContainer}>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Available</h4>
                            <Field className={css.hideAlreadyAssignedField}>
                                <Checkbox
                                    checked={hideAlreadyAssigned}
                                    onChange={e => setHideAlreadyAssigned(e.target.checked)}
                                >
                                    <Label>Hide already assigned</Label>
                                </Checkbox>
                            </Field>
                        </div>
                        <Field>
                            <Label hidden>Search Available</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search..."
                                isCompact
                                start={<SearchIcon />}
                                value={availableSearch}
                                onChange={e => setAvailableSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={availableSelection.allSelected}
                                    indeterminate={availableSelection.someSelected}
                                    onChange={e => availableSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAvailableProperties.length} of {availableProperties.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAvailable.map((p, index) => {
                            const currentJurisdiction = propertyJurisdictionMap.get(p.id);
                            return <div key={p.id} className={css.propertyItem}>
                                <Field>
                                    <Checkbox
                                        checked={availableSelection.isSelected(p)}
                                        onClick={event => availableSelection.handleSelect(event.currentTarget.checked, p, index, event.shiftKey)}
                                        onChange={(_event) => {/* shut up react warning */}}
                                    >
                                        <Label>
                                            <span className={css.propertyName}>{p.name}</span>
                                            {currentJurisdiction &&
                                                <span className={css.propertyJurisdiction}>Current: {currentJurisdiction}</span>
                                            }
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>;
                        })}
                    </div>
                </div>
                <div className={css.middleButtons}>
                    <Button onClick={assignProperties}><AssignIcon /></Button>
                    <Button onClick={unassignProperties}><UnassignIcon /></Button>
                </div>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Assigned</h4>
                        </div>
                        <Field>
                            <Label hidden>Search Assigned</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search..."
                                isCompact
                                start={<SearchIcon />}
                                value={assignedSearch}
                                onChange={e => setAssignedSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={assignedSelection.allSelected}
                                    indeterminate={assignedSelection.someSelected}
                                    onChange={e => assignedSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAssignedProperties.length} of {assignedProperties.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAssigned.map((p, index) =>
                            <div key={p.id} className={css.propertyItem}>
                                <Field>
                                    <Checkbox
                                        checked={assignedSelection.isSelected(p)}
                                        onClick={event => assignedSelection.handleSelect(event.currentTarget.checked, p, index, event.shiftKey)}
                                        onChange={(_event) => {/* shut up react warning */}}
                                    >
                                        <Label>
                                            <span className={css.propertyName}>{p.name}</span>
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Body>
        <Footer>
            <FooterItem>
                <Button onClick={() => props.closed(false)}>Cancel</Button>
            </FooterItem>
            <FooterItem>
                <Button isPrimary disabled={saving} onClick={doSave}>Save</Button>
            </FooterItem>
        </Footer>
    </Modal>;
}
